import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { scoresInFlow } from '../builder/services/flowUtilities'
import { funnelDefinitionEach } from '../services/funnelDefinitions'
import { questionsInFlow } from '@dashboard/modules/flow/services/flowUtilities'
import { stripHtml, renderText } from '@shared/services'
import { useFlowStore } from '@dashboard/modules/flow/stores/flowStore'
import { useQuestionStore } from '@dashboard/modules/segment/stores/questionStore'
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import { storeToRefs } from 'pinia'

export function useFlowLiquidVariables() {
  const route = useRoute()
  const flowStore = useFlowStore()
  const questionStore = useQuestionStore()
  const integrationStore = useIntegrationStore()
  const { integratedPartner } = storeToRefs(integrationStore)

  const availableLiquidVariables = computed(() => {
    const offerFunnelId = Array.isArray(route.params.offerFunnelId)
      ? route.params.offerFunnelId[0]
      : route.params.offerFunnelId

    if (!offerFunnelId) {
      return []
    }

    const flow = flowStore.getById(offerFunnelId)
    if (!flow) {
      return []
    }

    const variables: { liquid: string; description: string; category: 'visitor' | 'score' | 'variable' | 'input' }[] = [
      {
        liquid: '{{ visitor.email }}',
        description: "Visitor's email address (blank until they're identified)",
        category: 'visitor'
      }
    ]

    // Get scores from the flow
    const scores = scoresInFlow(flow.funnelDefinition, questionStore.questions)
    scores.forEach((score) => {
      variables.push({
        liquid: `{{ score.${score} }}`,
        description: `Score: ${score}`,
        category: 'score'
      })
    })

    // Get input questions from the flow
    const questions = questionsInFlow(flow.funnelDefinition)
    questions
      .filter((q) => q.displayType === 'input')
      .forEach((q) => {
        variables.push({
          liquid: `{{ input.${q.id} }}`,
          description: stripHtml(renderText(q.text)) || q.id,
          category: 'input'
        })
      })

    // Get variables that have been set in data nodes
    const dataVariables = new Set<string>()

    funnelDefinitionEach(flow.funnelDefinition, (node) => {
      if (node.type === 'data' && node.actions) {
        node.actions.forEach((action: any) => {
          if (action.type === 'variable' && action.variableName) {
            dataVariables.add(action.variableName)
          }
        })
      }
    })

    dataVariables.forEach((varName) => {
      variables.push({
        liquid: `{{ variable.${varName} }}`,
        description: `Variable: ${varName}`,
        category: 'variable'
      })
    })

    const categoryOrder = { visitor: 0, score: 1, variable: 2, input: 3 }
    return variables.sort((a, b) => categoryOrder[a.category] - categoryOrder[b.category])
  })

  // Show button if there are flow variables OR if there's an integrated partner
  // (which means there will be integration fields in the picker)
  const hasLiquidVariables = computed(() => availableLiquidVariables.value.length > 0 || !!integratedPartner.value)

  return {
    availableLiquidVariables,
    hasLiquidVariables
  }
}
